import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AlertController, IonicModule, ModalController, NavController } from '@ionic/angular';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { highlight } from 'highlight.js';
import _ from 'lodash';
import { ColorPickerModule } from 'ngx-color-picker';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { AdminWidgetRoutingModule } from 'src/app/admin/admin-widget/admin-widget-routing.module';
import { AutoresponderComponent } from 'src/app/components/autoresponder/autoresponder.component';
import { ChannelSelectorModule } from 'src/app/components/channel-selector/channel-selector.module';
import { SupportStepsModule } from 'src/app/components/support-steps/support-steps.module';
import {
  DEFAULT_INITIAL_SUPPORT_STEP,
  EXTERNAL_CHAT_CONNECTION_TYPES,
  FEATURES,
  INBOX_TYPES,
  routes,
} from 'src/app/constants';
import { Widget, InboundEmailRule } from 'src/models';
import { Project } from 'src/models/projects';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { AuthService } from 'src/services/auth.service';
import { InboundEmailrulesService } from 'src/services/inbound-email-rules.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { WidgetsService } from 'src/services/widgets.service';

const DEFAULT_COLOR = '#206cdf';
const DEFAULT_REPLY_TIME = 'in_a_few_hours';
const DEFAULT_BUBBLE_STYLE = 'standard';
const DEFAULT_BUBBLE_POSITION = 'right';

function getCodeForWidget(widgetId: string) {
  return `<script>
  (function (w, d, t) {
    w.$unthreadInAppChatSettings = {
      baseUrl: '${window.location.origin}',
      widgetId: '${widgetId}'
    };

    var u = function() {
      u.c(arguments);
    };
    u.q = [];
    u.c = function(args) {
      u.q.push(args);
    };

    if (!w.$unthread) {
      w.$unthread = {};
    }

    w.$unthread.inAppChat = u;

    var g = d.createElement(t), s = d.getElementsByTagName(t)[0];
    g.src = w.$unthreadInAppChatSettings.baseUrl + '/widget/js/wrapper.js';
    g.defer = true;
    g.async = true;
    s.parentNode.insertBefore(g, s);
  })(window, document, 'script');
</script>`;
}

function getCodeForHelpCenter(helpCenterId: string) {
  return `<script>
  (function (w, d, t) {
    w.$unthreadHelpCenterSettings = {
      baseUrl: '${window.location.origin}',
      helpCenterId: '${helpCenterId}'
    };

    var u = function() {
      u.c(arguments);
    };
    u.q = [];
    u.c = function(args) {
      u.q.push(args);
    };

    if (!w.$unthread) {
      w.$unthread = {};
    }

    w.$unthread.helpCenter = u;

    var g = d.createElement(t), s = d.getElementsByTagName(t)[0];
    g.src = w.$unthreadHelpCenterSettings.baseUrl + '/widget/js/hc-wrapper.js';
    g.defer = true;
    g.async = true;
    s.parentNode.insertBefore(g, s);
  })(window, document, 'script');
</script>`;
}

@Component({
  selector: 'app-edit-widget-channel',
  templateUrl: './edit-widget-channel.component.html',
  styleUrls: ['./edit-widget-channel.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    AdminWidgetRoutingModule,
    NgxDatatableModule,
    SharedPipesModule,
    SupportStepsModule,
    NgxTippyModule,
    ChannelSelectorModule,
    AutoresponderComponent,
    ColorPickerModule,
  ],
})
export class EditWidgetChannelComponent implements OnInit {
  verificationSecret: string;
  isLoading = false;
  supportSteps: any[] = [DEFAULT_INITIAL_SUPPORT_STEP];
  featureEnabled = true;
  code?: string;
  verificationSecretShown = false;
  leftTab: 'preview' | 'code' = 'preview';
  inboundEmailRules: Partial<InboundEmailRule>[] | null;
  widget: Partial<Widget> = {};
  isReplyPublic: boolean;
  isDarkModePreview: boolean;
  customerId: string;
  project?: Partial<Project>;
  hideProject?: boolean;

  constructor(
    public authService: AuthService,
    private msgSrvc: MessageService,
    private alertCtrl: AlertController,
    private navController: NavController,
    private inboundEmailRulesService: InboundEmailrulesService,
    private route: ActivatedRoute,
    private modalCtrl: ModalController,
    private widgetsService: WidgetsService,
    private searchablePopoverService: SearchablePopoverService,
  ) {}

  async ngOnInit() {
    this.isLoading = true;
    this.featureEnabled = !this.authService.restrictedFeatures.includes(FEATURES.WIDGET);
    this.isReplyPublic = this.authService.tenant.defaultToPublicMessages;

    if (this.customerId) {
      this.getWidget();
    } else {
      if (this.authService.selectedProject) {
        this.project = this.authService.selectedProject;
        this.hideProject = true;
      } else {
        const projectId = this.route.snapshot.params['projectId'] ?? this.authService.tenant.defaultProjectId;
        this.project = this.authService.projects.find((p) => p.id === projectId) as Project;
        this.hideProject = this.authService.projects.length === 1;
      }

      this.widget = {
        name: '',
        title: 'Hi there',
        tagline: 'Let us know if you have any questions',
        color: DEFAULT_COLOR,
        replyTime: DEFAULT_REPLY_TIME,
        bubbleStyle: DEFAULT_BUBBLE_STYLE,
        bubblePosition: DEFAULT_BUBBLE_POSITION,
        supportSteps: [DEFAULT_INITIAL_SUPPORT_STEP],
        channelId: '',
        linkedInboundEmailRuleId: null,
        isFullPageView: false,
        ticketTypesEnabled: false,
        darkMode: 'light',
        projectId: this.project?.id,
      };
    }

    this.isLoading = false;
  }

  async getInboundEmailRules() {
    const result = await this.inboundEmailRulesService.list({
      order: ['createdAt'],
      descending: false,
      where: this.project?.id
        ? [
            {
              field: 'projectId',
              operator: '==',
              value: this.project?.id,
            },
          ]
        : [],
    });

    this.inboundEmailRules = result.data ?? null;
  }

  async getWidget() {
    const result = await this.widgetsService.list({
      where: [{ field: 'customerId', operator: '==', value: this.customerId }],
    });

    if (result.data.length) {
      this.widget = result.data[0];
      this.widget.supportSteps = this.widget.customer?.supportSteps;

      this.getInboundEmailRules();
    } else {
      this.msgSrvc.showError('No widget found');
    }
  }

  getCodeHtmlRaw(widget: Partial<Widget>) {
    return widget.isFullPageView ? getCodeForHelpCenter(widget.id) : getCodeForWidget(widget.id);
  }

  getCodeHtml(widget: Partial<Widget>) {
    return highlight('html', this.getCodeHtmlRaw(widget)).value;
  }

  getIFrameHtmlRaw() {
    return '<iframe id="unthread_help_center" style="width: 100%; height: 100%; border: none; position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 999999;"></iframe>';
  }

  getIFrameHtml() {
    return highlight('html', this.getIFrameHtmlRaw()).value;
  }

  copyIFrameHtml() {
    navigator.clipboard
      .writeText(this.getIFrameHtmlRaw())
      .then(() => {
        this.msgSrvc.show('Copied!');
      })
      .catch(() => {
        // Unable to copy
      });
  }

  copyToClipboard(widget: Partial<Widget>) {
    navigator.clipboard
      .writeText(this.getCodeHtmlRaw(widget))
      .then(() => {
        this.msgSrvc.show('Copied!');
      })
      .catch(() => {
        // Unable to copy
      });
  }

  async save() {
    try {
      this.isLoading = true;
      const result = this.widget?.id
        ? await this.widgetsService.update(this.widget.id, this.widget)
        : await this.widgetsService.create(this.widget);
      this.isLoading = false;

      if (this.widget?.id) {
        this.dismiss();
      } else {
        this.widget = result;
        this.leftTab = 'code';
      }

      this.msgSrvc.show('✔︎ Saved');
    } catch (e) {
      this.isLoading = false;
      this.msgSrvc.showError(e);
    }
  }

  supportStepsDidChange(supportSteps: any[]) {
    this.widget.supportSteps = supportSteps;
  }

  async delete(widget: Partial<Widget>) {
    if (!widget || !widget.id) {
      return;
    }

    const alert = await this.alertCtrl.create({
      header: 'Disable chat widget',
      subHeader: 'Are you sure you want to disable the chat widget?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Yes',
          handler: async () => {
            this.isLoading = true;
            await this.widgetsService.delete(widget.id);
            this.dismiss();
            this.isLoading = false;

            this.navController.navigateRoot(
              `${routes.DASHBOARD}/${routes.CHANNELS}/${EXTERNAL_CHAT_CONNECTION_TYPES.IN_APP_CHAT}`,
            );
          },
        },
      ],
    });

    await alert.present();
  }

  toggleVerificationSecret() {
    this.verificationSecretShown = !this.verificationSecretShown;
  }

  showWidgetHelpdesk() {
    window.open('https://docs.unthread.io/in-app/chat-widget', '_blank');
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }

  onWidgetChannelChanged(slackChannel) {
    this.widget.channelId = slackChannel?.id ?? null;
    this.widget.slackChannel = slackChannel;

    if (this.widget.id) {
      this.save();
    }
  }

  viewInbox(rule: Partial<Widget>) {
    this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.INBOX}/${INBOX_TYPES.IN_APP}/${rule.id}`);
  }

  autoresponderDidChange(autoresponder: any) {
    this.widget.autoresponder = autoresponder;
  }

  async isReplyPublicDidChange() {
    try {
      await this.authService.updateTenant({
        defaultToPublicMessages: this.isReplyPublic,
      });
      this.msgSrvc.show('✔︎ Updated');
    } catch (err) {
      this.msgSrvc.show(`Something went wrong: ${err.message}`);
    }
  }

  selectProject() {
    this.searchablePopoverService.projects({
      event,
      showNoneOption: false,
      selectedProjectId: this.project?.id,
      callback: async (_id: string, _name: string, object: any) => {
        this.project = object;
      },
    });
  }
}
